
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, CheckCircle, InfoIcon, XCircle } from "lucide-react";
import { ProcessedGuard } from "./types";
import { ResultsList } from "./ResultsList";

interface ResultTabsProps {
  createdGuards: ProcessedGuard[];
  updatedGuards: ProcessedGuard[];
  warningGuards: ProcessedGuard[];
  failedGuards: ProcessedGuard[];
  defaultTab: string;
}

export const ResultTabs = ({ 
  createdGuards, 
  updatedGuards, 
  warningGuards, 
  failedGuards, 
  defaultTab 
}: ResultTabsProps) => {
  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <TabsList className="grid grid-cols-4 mb-4">
        <TabsTrigger value="created" className="relative">
          Created
          {createdGuards.length > 0 && (
            <Badge variant="secondary" className="ml-1.5">
              {createdGuards.length}
            </Badge>
          )}
        </TabsTrigger>
        
        <TabsTrigger value="updated" className="relative">
          Updated
          {updatedGuards.length > 0 && (
            <Badge variant="secondary" className="ml-1.5">
              {updatedGuards.length}
            </Badge>
          )}
        </TabsTrigger>
        
        <TabsTrigger value="warnings" className="relative">
          Warnings
          {warningGuards.length > 0 && (
            <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-700">
              {warningGuards.length}
            </Badge>
          )}
        </TabsTrigger>
        
        <TabsTrigger value="failed" className="relative">
          Failed
          {failedGuards.length > 0 && (
            <Badge variant="destructive" className="ml-1.5">
              {failedGuards.length}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="created" className="mt-0">
        <ResultsList
          items={createdGuards}
          icon={<CheckCircle className="h-5 w-5 text-green-500" />}
          emptyMessage="No guards were created"
        />
      </TabsContent>
      
      <TabsContent value="updated" className="mt-0">
        <ResultsList
          items={updatedGuards}
          icon={<InfoIcon className="h-5 w-5 text-blue-500" />}
          emptyMessage="No guards were updated"
          showAFM
        />
      </TabsContent>
      
      <TabsContent value="warnings" className="mt-0">
        <ResultsList
          items={warningGuards}
          icon={<AlertCircle className="h-5 w-5 text-amber-500" />}
          emptyMessage="No warnings were reported"
          showWarning
          showAFM
        />
      </TabsContent>
      
      <TabsContent value="failed" className="mt-0">
        <ResultsList
          items={failedGuards}
          icon={<XCircle className="h-5 w-5 text-red-500" />}
          emptyMessage="No guards failed to process"
          showError
          showAFM
        />
      </TabsContent>
    </Tabs>
  );
};
